import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { MagicInput as Input } from '@/components/ui/magic-input'
import { Label } from '@/components/ui/label'
import { createTicketPaymentIntentFn } from '@/lib/firebase/functions'
import { formatPrice } from './TicketTypeSelector'
import type { CheckoutItem } from '../types'
import { getStripePromise } from '@/lib/stripe'

// ── Purchaser info schema ──────────────────────────────────────────────────────

const purchaserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  termsAccepted: z.boolean().refine((value) => value, {
    message: 'You must agree before continuing',
  }),
})

type PurchaserValues = z.infer<typeof purchaserSchema>

// ── Step 2: Stripe payment form ───────────────────────────────────────────────

interface PaymentStepProps {
  orderId: string
  receiptToken: string
  total: number
  onSuccess: (orderId: string, receiptToken: string) => void
}

function PaymentStep({ orderId, receiptToken, total, onSuccess }: PaymentStepProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/tickets/success?${new URLSearchParams({
          orderId,
          receiptToken,
        }).toString()}`,
      },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(formatUserFacingError(
        stripeError,
        'We could not complete your payment. Please check your details and try again.',
        'payments/ticket-confirmation',
      ))
      setProcessing(false)
    } else {
      onSuccess(orderId, receiptToken)
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <HoldConfirmButton type="submit" disabled={!stripe || processing} className="w-full">
        {processing ? 'Processing…' : `Pay ${formatPrice(total)}`}
      </HoldConfirmButton>
    </form>
  )
}

// ── Main CheckoutForm ─────────────────────────────────────────────────────────

interface CheckoutFormProps {
  eventId: string
  items: CheckoutItem[]
  onSuccess: (orderId: string, receiptToken: string) => void
}

export function CheckoutForm({ eventId, items, onSuccess }: CheckoutFormProps) {
  const [checkoutId] = useState(() => crypto.randomUUID())
  const [step, setStep] = useState<'info' | 'payment'>('info')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [receiptToken, setReceiptToken] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const total = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PurchaserValues>({
    resolver: zodResolver(purchaserSchema),
    defaultValues: { firstName: '', lastName: '', email: '', termsAccepted: false },
  })

  async function onInfoSubmit(values: PurchaserValues) {
    setApiError(null)
    try {
      const result = await createTicketPaymentIntentFn({
        checkoutId,
        eventId,
        items: items.map((i) => ({ ticketTypeId: i.ticketTypeId, quantity: i.quantity })),
        purchaserEmail: values.email,
        purchaserFirstName: values.firstName,
        purchaserLastName: values.lastName,
        termsAccepted: values.termsAccepted,
      })
      setClientSecret(result.data.clientSecret)
      setOrderId(result.data.orderId)
      setReceiptToken(result.data.receiptToken)
      setStep('payment')
    } catch (err: unknown) {
      setApiError(formatUserFacingError(err, 'Failed to initialize checkout'))
    }
  }

  if (step === 'info') {
    return (
      <form onSubmit={(e) => void handleSubmit(onInfoSubmit)(e)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="firstName">First Name</Label>
            <Input id="firstName" {...register('firstName')} />
            {errors.firstName && (
              <p className="text-xs text-destructive">{withErrorCode(String(errors.firstName.message), 'app/validation')}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="lastName">Last Name</Label>
            <Input id="lastName" {...register('lastName')} />
            {errors.lastName && (
              <p className="text-xs text-destructive">{withErrorCode(String(errors.lastName.message), 'app/validation')}</p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register('email')} />
          {errors.email && <p className="text-xs text-destructive">{withErrorCode(String(errors.email.message), 'app/validation')}</p>}
        </div>
        <div className="rounded-xl border bg-muted/30 p-4">
          <div className="flex items-start gap-3">
            <input
              id="ticket-terms-accepted-native"
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
              {...register('termsAccepted')}
            />
            <div>
              <Label htmlFor="ticket-terms-accepted-native" className="cursor-pointer font-normal leading-5">
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
                  Terms of Service
                </Link>{' '}
                and acknowledge the{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
                  Privacy Policy
                </Link>
                .
              </Label>
              {errors.termsAccepted && (
                <p className="mt-1 text-xs text-destructive">{errors.termsAccepted.message}</p>
              )}
            </div>
          </div>
        </div>
        {apiError && <p className="text-sm text-destructive">{apiError}</p>}
        <HoldConfirmButton type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? 'Please wait…' : 'Continue to Payment'}
        </HoldConfirmButton>
      </form>
    )
  }

  if (!clientSecret || !orderId || !receiptToken) {
    return <p className="text-sm text-muted-foreground">Loading payment form…</p>
  }

  return (
    <Elements
      stripe={getStripePromise()}
      options={{ clientSecret, appearance: { theme: 'stripe' } }}
    >
      <PaymentStep
        orderId={orderId}
        receiptToken={receiptToken}
        total={total}
        onSuccess={onSuccess}
      />
    </Elements>
  )
}
